import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import serviceManagementService from '../../../services/serviceManagementService';

const RegionalCustomization = ({ country, serviceAvailability, onRefresh, loading, language }) => {
  const [editingService, setEditingService] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [regulatoryNotes, setRegulatoryNotes] = useState('');
  const [saving, setSaving] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="text-center py-12">
        <Icon name="Globe" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Sélectionnez un pays pour personnaliser les services</p>
      </div>
    );
  }

  const handleEditService = (service) => {
    setEditingService(service);
    setCustomPrice(service?.customPrice?.toString() || '');
    setRegulatoryNotes(service?.regulatoryNotes || '');
  };

  const handleSaveCustomization = async (serviceCountryId) => {
    try {
      setSaving(true);
      await serviceManagementService?.updateServiceCountryAvailability(serviceCountryId, {
        customPrice: customPrice ? parseFloat(customPrice) : null,
        regulatoryNotes: regulatoryNotes || null
      });
      setEditingService(null);
      setCustomPrice('');
      setRegulatoryNotes('');
      onRefresh();
    } catch (error) {
      console.error('Error saving customization:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setCustomPrice('');
    setRegulatoryNotes('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Personnalisation Régionale - {country?.countryName}
        </h3>
        <p className="text-sm text-muted-foreground">
          Configurez les prix et les notes réglementaires spécifiques à ce pays
        </p>
      </div>

      <div className="space-y-3">
        {serviceAvailability?.map((service) => (
          <div
            key={service?.id}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            {editingService?.id === service?.id ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-foreground">{service?.serviceName}</h4>
                    <p className="text-xs text-muted-foreground">{service?.serviceCode}</p>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Prix de base: {service?.basePrice} {country?.currencyCode}
                  </span>
                </div>

                <Input
                  label={`Prix Personnalisé (${country?.currencyCode})`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e?.target?.value)}
                  placeholder={`${service?.basePrice} (prix par défaut)`}
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes Réglementaires
                  </label>
                  <textarea
                    value={regulatoryNotes}
                    onChange={(e) => setRegulatoryNotes(e?.target?.value)}
                    placeholder="Exigences réglementaires spécifiques..."
                    rows={3}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleSaveCustomization(service?.id)}
                    loading={saving}
                    size="sm"
                  >
                    Enregistrer
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    size="sm"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-foreground">{service?.serviceName}</h4>
                    <span className="text-xs font-mono text-muted-foreground">
                      {service?.serviceCode}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {service?.category}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Prix de base:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {service?.basePrice} {country?.currencyCode}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prix personnalisé:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {service?.customPrice ? `${service?.customPrice} ${country?.currencyCode}` : 'Non défini'}
                      </span>
                    </div>
                  </div>
                  {service?.regulatoryNotes && (
                    <div className="mt-2 p-2 bg-muted/50 rounded text-sm text-muted-foreground">
                      <Icon name="Info" size={14} className="inline mr-1" />
                      {service?.regulatoryNotes}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => handleEditService(service)}
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                >
                  Modifier
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {serviceAvailability?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Aucun service configuré pour ce pays</p>
        </div>
      )}
    </div>
  );
};

export default RegionalCustomization;